import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Percent,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { MyCommissionResponse } from '@aeris/contracts'
import { parseAmount } from '@aeris/finance'
import { formatMoney } from '@aeris/money'
import { rateLabel } from '@aeris/printing'
import { Badge, Button, InlineAlert, Spinner } from '@aeris/ui'

import {
  dayOf,
  fetchMyCommission,
  fetchMyCommissionLines,
  periodLabel,
  periodOf,
  shiftPeriod,
} from '../lib/api/commissions.ts'
import { useAuth } from '../lib/auth-context.ts'
import { describeCommissionLine, itemLabel } from '../lib/commission-lines.ts'

/**
 * "Minhas vendas" no Início (F137, F215).
 *
 * O que o vendedor pediu para saber sem perguntar a ninguém: quanto já vendeu,
 * quanto vendeu cada dia, e a comissão. Só aparece para quem tem % — um caixa
 * sem comissão não precisa de um cartão com zero — e só com os números dele: a
 * rota lê o vendedor da sessão.
 *
 * A conta é a da comissão: a venda concluída ou o pedido entregue, menos a
 * troca que virou vale no dia da troca. A troca por outra peça não desconta
 * (F161).
 */

const money = (wire: string): string => formatMoney(parseAmount(wire))

const hasValue = (wire: string): boolean => /[1-9]/.test(wire)

const count = (value: number): string =>
  `${String(value)} ${value === 1 ? 'venda' : 'vendas'}`

/** `2026-09-30` → `30/09 · ter.` */
const dayLabel = (date: string): string => {
  const [year = '1970', month = '01', day = '01'] = date.split('-')
  const weekday = new Intl.DateTimeFormat('pt-BR', { weekday: 'short' }).format(
    new Date(
      Number.parseInt(year, 10),
      Number.parseInt(month, 10) - 1,
      Number.parseInt(day, 10),
      12
    )
  )
  return `${day}/${month} · ${weekday}`
}

const timeOf = (value: string): string =>
  new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))

type Day = MyCommissionResponse['days'][number]

/** As vendas de um dia, buscadas quando o dia é aberto. */
const DayLines = ({
  period,
  date,
}: {
  period: string
  date: string
}): ReactNode => {
  const lines = useQuery({
    queryKey: ['commissions', 'me', 'lines', period],
    queryFn: ({ signal }) => fetchMyCommissionLines(period, signal),
  })

  if (lines.isPending) {
    return (
      <p className="flex items-center gap-2 py-2 text-(length:--text-sm) text-(--text-muted)">
        <Spinner />
        Carregando as vendas do dia…
      </p>
    )
  }
  if (lines.isError) {
    return (
      <InlineAlert tone="danger">
        Não foi possível carregar as vendas do dia.
      </InlineAlert>
    )
  }

  const ofDay = lines.data.lines.filter(
    (line) => dayOf(line.occurredAt) === date
  )

  return (
    <ul
      aria-label={`Vendas de ${dayLabel(date)}`}
      className="flex flex-col divide-y divide-(--border-subtle)"
    >
      {ofDay.map((line) => (
        <li
          key={`${line.sourceType}-${line.sourceId}`}
          className="flex items-start justify-between gap-3 py-2 text-(length:--text-sm)"
        >
          <span className="flex min-w-0 flex-col">
            <span className="truncate">
              {`${timeOf(line.occurredAt)} · ${describeCommissionLine(line)}`}
            </span>
            {line.items.length === 0 ? null : (
              <span className="text-(length:--text-xs) text-(--text-secondary)">
                {line.items.map(itemLabel).join(' · ')}
              </span>
            )}
          </span>
          <span
            className={
              line.baseAmount.startsWith('-')
                ? 'shrink-0 font-(--font-mono) tabular-nums text-(--danger)'
                : 'shrink-0 font-(--font-mono) tabular-nums'
            }
          >
            {money(line.baseAmount)}
          </span>
        </li>
      ))}
    </ul>
  )
}

const DayRow = ({
  day,
  period,
  open,
  onToggle,
}: {
  day: Day
  period: string
  open: boolean
  onToggle: () => void
}): ReactNode => (
  <>
    <tr className="border-t border-(--border-subtle)">
      <td className="py-1.5 pr-2">
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className="flex items-center gap-1 rounded-(--radius-sm) text-left font-(--weight-medium) text-(--text-primary) hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--border-focus)"
        >
          {open ? (
            <ChevronUp aria-hidden="true" className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
          )}
          {dayLabel(day.date)}
        </button>
        {day.date.startsWith(period) ? null : (
          <Badge tone="warning">de mês anterior</Badge>
        )}
      </td>
      <td className="py-1.5 pr-2 text-right tabular-nums">
        {String(day.salesCount)}
      </td>
      <td className="py-1.5 pr-2 text-right font-(--font-mono) tabular-nums">
        {money(day.salesAmount)}
      </td>
      <td className="py-1.5 pr-2 text-right font-(--font-mono) tabular-nums text-(--danger)">
        {hasValue(day.returnsAmount) ? `−${money(day.returnsAmount)}` : ''}
      </td>
      <td
        className={
          day.commissionAmount.startsWith('-')
            ? 'py-1.5 text-right font-(--font-mono) tabular-nums text-(--danger)'
            : 'py-1.5 text-right font-(--font-mono) tabular-nums'
        }
      >
        {money(day.commissionAmount)}
      </td>
    </tr>
    {open ? (
      <tr>
        <td colSpan={5} className="pb-2 pl-5">
          <DayLines period={period} date={day.date} />
        </td>
      </tr>
    ) : null}
  </>
)

export const MyCommissionCard = (): ReactNode => {
  const { allows } = useAuth()
  const currentPeriod = periodOf(new Date())
  const [period, setPeriod] = useState(currentPeriod)
  const [openDay, setOpenDay] = useState<string | null>(null)

  const mine = useQuery({
    queryKey: ['commissions', 'me', period],
    queryFn: ({ signal }) => fetchMyCommission(period, signal),
    enabled: allows('commission.read.own'),
    refetchInterval: 5 * 60_000,
    // Trocar de mês não some com o cartão enquanto o outro mês chega.
    placeholderData: keepPreviousData,
  })

  const data = mine.data
  if (data?.currentRate == null || !/[1-9]/.test(data.currentRate)) {
    return null
  }

  const closed = data.statement
  const today =
    period === currentPeriod
      ? data.days.find((day) => day.date === dayOf(new Date()))
      : undefined

  const move = (months: number): void => {
    setPeriod((value) => shiftPeriod(value, months))
    setOpenDay(null)
  }

  return (
    <section
      aria-label="Minhas vendas"
      className="flex flex-col gap-3 rounded-(--radius-lg) border border-(--border-default) bg-(--surface) p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-(length:--text-sm) font-(--weight-semibold) text-(--text-secondary)">
          <Percent aria-hidden="true" className="h-4 w-4" />
          Minhas vendas
        </h2>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Mês anterior"
            leadingIcon={<ChevronLeft aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              move(-1)
            }}
          />
          <span
            aria-live="polite"
            className="min-w-32 text-center text-(length:--text-sm) font-(--weight-semibold) capitalize"
          >
            {periodLabel(period)}
          </span>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Próximo mês"
            disabled={period >= currentPeriod}
            leadingIcon={
              <ChevronRight aria-hidden="true" className="h-4 w-4" />
            }
            onClick={() => {
              move(1)
            }}
          />
          <Badge tone="info">{rateLabel(data.currentRate)}</Badge>
        </div>
      </div>

      {period === currentPeriod ? (
        <p
          aria-label="Hoje"
          className="text-(length:--text-sm) text-(--text-primary)"
        >
          <span className="font-(--weight-semibold)">Hoje: </span>
          {today === undefined
            ? 'nenhuma venda ainda.'
            : `${money(today.salesAmount)} em ${count(today.salesCount)} · comissão ${money(today.commissionAmount)}`}
        </p>
      ) : null}

      <div className="flex flex-col gap-1">
        <span className="text-(length:--text-xs) text-(--text-muted)">
          Comissão do mês
        </span>
        <p className="font-(--font-mono) text-(length:--text-2xl) font-(--weight-semibold) tabular-nums text-(--text-primary)">
          {money(
            closed === null ? data.commissionAmount : closed.commissionAmount
          )}
        </p>
        <p className="text-(length:--text-sm) text-(--text-secondary)">
          {`${count(data.salesCount)} · ${money(data.salesAmount)}`}
          {hasValue(data.returnsAmount)
            ? ` − trocas ${money(data.returnsAmount)}`
            : ''}
          {` = base ${money(data.baseAmount)}`}
        </p>
      </div>

      {data.days.length === 0 ? (
        <p className="text-(length:--text-sm) text-(--text-muted)">
          Nenhuma venda neste mês.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table
            aria-label="Dia a dia"
            className="w-full text-(length:--text-sm)"
          >
            <thead>
              <tr className="text-(length:--text-xs) text-(--text-muted)">
                <th scope="col" className="pb-1 text-left font-normal">
                  Dia
                </th>
                <th scope="col" className="pb-1 text-right font-normal">
                  Vendas
                </th>
                <th scope="col" className="pb-1 text-right font-normal">
                  Vendido
                </th>
                <th scope="col" className="pb-1 text-right font-normal">
                  Trocas
                </th>
                <th scope="col" className="pb-1 text-right font-normal">
                  Comissão
                </th>
              </tr>
            </thead>
            <tbody>
              {data.days.map((day) => (
                <DayRow
                  key={day.date}
                  day={day}
                  period={period}
                  open={openDay === day.date}
                  onToggle={() => {
                    setOpenDay((value) =>
                      value === day.date ? null : day.date
                    )
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-(length:--text-xs) text-(--text-muted)">
        {closed === null
          ? 'Até agora. O valor fecha depois do último dia do mês.'
          : 'Mês fechado: é este o valor que vai ser pago.'}
      </p>
    </section>
  )
}
